/**
 * Display text safe to put in a table cell or heading: user-chosen names
 * (organization, owner, member, location) are untrusted. React already
 * escapes markup, so this removes what escaping cannot.
 *
 * Stripped (the Unicode "default ignorable" set plus controls):
 * - bidirectional controls (U+200E/F, U+202A-E, U+2066-9, U+061C), which
 *   reorder the text around a name and make it read as something else;
 * - invisible fillers that fake a blank or different name (zero-width space,
 *   word joiner, Hangul fillers U+115F/1160/3164/FFA0, U+034F, U+180B-F, tag
 *   characters, soft hyphen, BOM), braille blank U+2800 and the interlinear
 *   annotation marks U+FFF9-FFFC;
 * - C0/C1 controls and private-use/surrogate code points.
 * ZWJ and ZWNJ (emoji sequences, Persian) and variation selectors U+FE0E/F
 * (emoji presentation) are legitimate and kept. A name with no visible
 * character left reads as empty so callers show their fallback.
 */
const WHITESPACE_RUN = /[\s\u0085]+/g;
const STRIPPED = /[\p{Default_Ignorable_Code_Point}\p{Cc}\p{Cs}\p{Co}\u2800\uFFF9-\uFFFC]/gu;
const KEPT = new Set(["\u200C", "\u200D", "\uFE0E", "\uFE0F"]);
const VISIBLE = /[\p{L}\p{N}\p{P}\p{S}\p{Extended_Pictographic}]/u;

/** Longest name rendered, in code points; a longer one is cut with an ellipsis. */
export const DISPLAY_TEXT_MAX_LENGTH = 200;

export function sanitizeDisplayText(value: string | null | undefined): string {
  if (typeof value !== "string") return "";
  const text = value
    .replace(WHITESPACE_RUN, " ")
    .replace(STRIPPED, (ch) => (KEPT.has(ch) ? ch : ""))
    .replace(WHITESPACE_RUN, " ")
    .trim();
  if (!VISIBLE.test(text)) return "";
  const points = Array.from(text);
  if (points.length <= DISPLAY_TEXT_MAX_LENGTH) return text;
  return points.slice(0, DISPLAY_TEXT_MAX_LENGTH).join("").trimEnd() + "\u2026";
}
